import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Business, Mask, User } from '@app/models/backend';

// -- Services -- //
import { EnumService, HelperService, NotificationService, RegisterService, UserService } from '@app/core/services';
import { NgxSpinnerService } from 'ngx-spinner';

import { markFormGroupTouched, regex, regexErrors } from '@app/shared';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';

import { MaskModalComponent } from '@app/pages/home/pages/profile/components';
import { BusinessVisit } from '@app/models/backend/business-visit';


@Component({
  selector: 'app-business-detail',
  templateUrl: './business-detail.component.html',
  styleUrls: ['./business-detail.component.scss']
})
export class BusinessDetailComponent implements OnInit {

  businessForm!: FormGroup;
  businessValue!: Business;

  // ---- Regex Pattern ---- //
  regexErrors = regexErrors;

  // ---- Input and Output ---- //
  @Input() businessInfo!: Business;
  @Output() emitBusinessForm = new EventEmitter();
  @Input() metaData: any;

  @Output() submitUpdateProfile: EventEmitter<any> = new EventEmitter();
  @Input() profile: any;
  @Input() isLeadView: boolean = false;

  // ---- Variables ---- //
  provinceCities: any[] = [];
  isEditMode: boolean = false;
  isCreate: boolean = false;


  userProfile!: User;
  business!: Business;
  partyMasks!: Mask[];
  businessVisit !: BusinessVisit[];

  constructor(private userService: UserService,
              private registerService: RegisterService,
              private spinner: NgxSpinnerService,
              private router: Router,
              private notification: NotificationService,
              private route: ActivatedRoute,
              private fb: FormBuilder,
              private modal: BsModalService,
              public enumService: EnumService,
              public helperService: HelperService) { }

  ngOnInit(): void {
    this.initForm();

    // ---- For EditMode ---- //
    if( this.router.url === '/home/profile' || this.router.url.includes('/home/profile')) {
      this.isEditMode = true;
    }

    // ---- Selected Province Show Cities Otherwise Hide ---- //
    if (this.isEditMode) {
      this.businessForm.controls.provinceId.valueChanges.subscribe((res: any) => {
        this.selectProvince(this.businessForm.value.provinceId || this.business?.provinceId);
      });
    }

    if (this.isLeadView) {
      this.businessForm.disable();
    }
  }

  // -- ngOnChanges -- //
  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.profile && this.profile ) {
      this.business = this.profile['business'] as Business;
      this.userProfile = this.profile['user'] as User;
      this.partyMasks = this.profile['partyMasks'] as Mask[];
      this.businessVisit = this.profile['businessVisits'] as BusinessVisit[];

      this.businessForm.patchValue(this.business);
      this.selectProvince(this.businessForm.value.provinceId);

      if (this.userProfile) {
        if (!this.userProfile?.isEditable) {
          this.businessForm.disable();
        }
      }
    }
    if (simpleChanges.businessInfo && this.business) {
      this.businessForm.patchValue(this.business);
      this.selectProvince(this.businessForm.value.provinceId);
    }
  }

  // ---- On Submit ----- //
  onSubmit() {
    let form = this.businessForm.value

    if(this.businessForm.valid) {
        this.businessValue = {
          businessId: form.businessId,
          name: form.name,
          email: form.email,
          ntn: form.ntn,
          officePhone: form.officePhone,
          businessPhone: form.businessPhone,
          area: form.area,
          subArea: form.subArea,
          provinceId: form.provinceId,
          cityId: form.cityId,
          isActive: false,
          balance: 0
        }

        this.emitBusinessForm.emit(this.businessValue)
    } else {
        this.emitBusinessForm.emit(null);
        markFormGroupTouched(this.businessForm);
        return;
    }
  }

  // -- Update Profile -- //
  updateProfile() {
    let form = this.businessForm.value;
    if (this.businessForm.valid) {
      this.spinner.show();
      const business: Business =
      {
        businessId: form.businessId,
        name: form.name,
        email: form.email,
        ntn: form.ntn,
        officePhone: form.officePhone,
        businessPhone: form.businessPhone,
        area: form.area,
        subArea: form.subArea,
        provinceId: Number(form.provinceId),
        cityId: Number(form.cityId),
        isActive: false,
        balance: 0
      }
      this.submitUpdateProfile.emit(business);
    } else {
      markFormGroupTouched(this.businessForm);
      return;
    }
  }

  // -- select Province -- //
  selectProvince(value: any) {
    let province = value;
    if (value) {
      this.provinceCities = this.metaData?.city.filter((city: any) => {
        return city.provinceId == province;
      }) || [];
    }
  }

  // -- Open Mask Modal -- //
  openMaskModal() {
    const modalRef = this.modal.show(MaskModalComponent);
    modalRef.content!.user = this.userProfile;
    modalRef.content!.partyMasks = this.partyMasks;
    modalRef.content!.mask = <Mask>{};
  }

  // -- Open Mask Edit Modal -- //
  openMaskEditModal(currMask: Mask) {
    const modalRef = this.modal.show(MaskModalComponent);
    modalRef.content!.user = this.userProfile;
    modalRef.content!.partyMasks = this.partyMasks;
    modalRef.content!.mask = currMask;
    modalRef.content!.form.patchValue(currMask);
    modalRef.content!.isEditMode = true;
  }


   // ---- init Business Form ---- //
   initForm(): void {
    this.businessForm = this.fb.group({
      businessId: [0],
      name: ['', {
        updateOn: 'change', validators: [
          Validators.required,
          Validators.maxLength(128),
          Validators.pattern(regex.latinAndSpaces)
        ]
      }],
      email: ['', {
        updateOn: 'change', validators: [
          Validators.maxLength(128),
          Validators.pattern(regex.email)
        ]
      }],
      provinceId: ['', {
        updateOn: 'change', validators: [
          Validators.required
        ]
      }],
      businessPhone: ['', {
        updateOn: 'change', validators: [
          Validators.required,
          Validators.pattern(regex.phone)
        ]
      }],
      cityId: ['', {
        updateOn: 'change',
        validators: [
          Validators.required
        ]
      }],
      officePhone: ['', {
        updateOn: 'change'
      }],
      area: ['', {
        updateOn: 'change', validators: [
          Validators.required
        ]
      }],
      ntn: ['', {
        updateOn: 'change'
      }],
      subArea: ['', {
        updateOn: 'change', validators: [
          Validators.required
        ]
      }],
      ownerName: [null, {
        updateOn: 'change'
      }],
      cnic: [null, {
        updateOn: 'change'
      }],
      landmark: [null, {
        updateOn: 'change'
      }],
      industryId: ['', {
        updateOn: 'change'
      }],
      address: [null, {
        updateOn: 'change'
      }],
      comments: [null, {
        updateOn: 'change'
      }],
      customerFeedback: [null, {
        updateOn: 'change'
      }],
      partyTierId: [null, {
        updateOn: 'change'
      }]
    });
  }

}
